/**
 * The expression language of the constraint layout, as the legacy `ConstraintLayout` uses it:
 * numbers, names with member access (`gauge.center.X`), `+ - * / %`, unary minus, parentheses
 * and function calls (`min(a, b)`, also as `Math.Min(a, b)`). Names and members are matched
 * without regard to case, as in the legacy evaluator. There is no `eval`.
 *
 * For conditions (a tile's style rules, dashboard-editor spec §5.10 SR-4), also comparisons
 * (`< <= > >= == = !=`) and `and`, `or`, `not` (or `&& || !`), below the arithmetic: a comparison
 * gives 1 or 0, and a value holds when it is not 0. `or` and `and` give 1 or 0 too.
 */

/** A value in an expression: a number or an object whose members are values. */
export type Value = number | { readonly [member: string]: Value };

export type Scope = Readonly<Record<string, Value>>;

type Node =
    | { kind: 'number'; value: number }
    | { kind: 'path'; names: string[] }
    | { kind: 'call'; name: string; args: Node[] }
    | { kind: 'negate'; operand: Node }
    | { kind: 'binary'; op: '+' | '-' | '*' | '/' | '%'; left: Node; right: Node }
    | { kind: 'compare'; op: Comparison; left: Node; right: Node }
    | { kind: 'logic'; op: 'and' | 'or'; left: Node; right: Node }
    | { kind: 'not'; operand: Node };

type Comparison = '<' | '<=' | '>' | '>=' | '==' | '!=';
const COMPARISONS: readonly string[] = ['<', '<=', '>', '>=', '==', '=', '!='];

export class ExpressionError extends Error {
    override name = 'ExpressionError';
}

const FUNCTIONS: Readonly<Record<string, (...args: number[]) => number>> = {
    min: Math.min,
    max: Math.max,
    sin: Math.sin,
    cos: Math.cos,
    abs: Math.abs,
    sqrt: Math.sqrt,
    round: Math.round,
    floor: Math.floor,
    ceiling: Math.ceil,
    ceil: Math.ceil,
    // For a tile's transform, which is where units are converted (tiles spec TU-3).
    ftoc: (fahrenheit) => ((fahrenheit - 32) * 5) / 9,
    ctof: (celsius) => (celsius * 9) / 5 + 32,
};

type Token = { type: 'number'; value: number } | { type: 'name'; value: string } | { type: 'symbol'; value: string };

function tokenize(source: string) : Token[] {
    const tokens: Token[] = [];
    const pattern = /\s*(?:(\d+(?:\.\d*)?|\.\d+)|([A-Za-z_][A-Za-z0-9_]*)|(<=|>=|==|!=|&&|\|\||[-+*/%().,<>=!]))/y;
    let index = 0;
    while (index < source.length) {
        if (/^\s*$/.test(source.slice(index))) {
            break;
        }

        pattern.lastIndex = index;
        const match = pattern.exec(source);
        if (!match) {
            throw new ExpressionError(`unexpected character at ${index} in "${source}"`);
        }
        if (match[1] !== undefined) {
            tokens.push({ type: 'number', value: Number(match[1]) });
        } else if (match[2] !== undefined) {
            tokens.push({ type: 'name', value: match[2] });
        } else {
            tokens.push({ type: 'symbol', value: match[3]! });
        }
        index = pattern.lastIndex;
    }
    return tokens;
}

function parse(source: string) : Node {
    const tokens = tokenize(source);
    let position = 0;
    const peek = () : Token | undefined => tokens[position];
    const isSymbol = (value: string) : boolean => peek()?.type === 'symbol' && peek()!.value === value;
    const expect = (value: string) : void => {
        if (!isSymbol(value)) {
            throw new ExpressionError(`expected "${value}" in "${source}"`);
        }

        position++;
    };

    // Lowest first: or, and, not, a comparison, then the arithmetic below it.
    const isWord = (word: string) : boolean => peek()?.type === 'name' && String(peek()!.value).toLowerCase() === word;

    function disjunction() : Node {
        let node = conjunction();
        while (isSymbol('||') || isWord('or')) {
            position++;
            node = { kind: 'logic', op: 'or', left: node, right: conjunction() };
        }
        return node;
    }

    function conjunction() : Node {
        let node = negation();
        while (isSymbol('&&') || isWord('and')) {
            position++;
            node = { kind: 'logic', op: 'and', left: node, right: negation() };
        }
        return node;
    }

    function negation() : Node {
        if (isSymbol('!') || isWord('not')) {
            position++;

            return { kind: 'not', operand: negation() };
        }
        return comparison();
    }

    function comparison() : Node {
        const left = expression();
        const token = peek();
        if (token?.type === 'symbol' && COMPARISONS.includes(token.value)) {
            position++;
            const op = (token.value === '=' ? '==' : token.value) as Comparison;

            return { kind: 'compare', op, left, right: expression() };
        }
        return left;
    }

    function expression() : Node {
        let node = term();
        while (isSymbol('+') || isSymbol('-')) {
            const op = (tokens[position++] as { value: '+' | '-' }).value;
            node = { kind: 'binary', op, left: node, right: term() };
        }
        return node;
    }

    function term() : Node {
        let node = unary();
        while (isSymbol('*') || isSymbol('/') || isSymbol('%')) {
            const op = (tokens[position++] as { value: '*' | '/' | '%' }).value;
            node = { kind: 'binary', op, left: node, right: unary() };
        }
        return node;
    }

    function unary() : Node {
        if (isSymbol('-')) {
            position++;

            return { kind: 'negate', operand: unary() };
        }
        if (isSymbol('+')) {
            position++;

            return unary();
        }
        return primary();
    }

    function primary() : Node {
        const token = peek();
        if (!token) {
            throw new ExpressionError(`unexpected end of "${source}"`);
        }
        if (token.type === 'number') {
            position++;

            return { kind: 'number', value: token.value };
        }
        if (isSymbol('(')) {
            position++;
            const node = disjunction();
            expect(')');

            return node;
        }
        if (token.type === 'name') {
            const names = [token.value];
            position++;
            while (isSymbol('.')) {
                position++;
                const next = peek();
                if (next?.type !== 'name') {
                    throw new ExpressionError(`expected a member name in "${source}"`);
                }

                names.push(next.value);
                position++;
            }
            if (isSymbol('(')) {
                position++;
                const args: Node[] = [];
                if (!isSymbol(')')) {
                    args.push(expression());
                    while (isSymbol(',')) {
                        position++;
                        args.push(expression());
                    }
                }
                expect(')');
                // `Math.Min(...)` and `min(...)` are the same function.
                if (names.length > 2 || (names.length === 2 && names[0]!.toLowerCase() !== 'math')) {
                    throw new ExpressionError(`unknown function "${names.join('.')}"`);
                }

                const name = names.at(-1)!.toLowerCase();
                if (!(name in FUNCTIONS)) {
                    throw new ExpressionError(`unknown function "${name}"`);
                }

                return { kind: 'call', name, args };
            }
            return { kind: 'path', names };
        }
        throw new ExpressionError(`unexpected "${token.value}" in "${source}"`);
    }

    const node = disjunction();
    if (position < tokens.length) {
        throw new ExpressionError(`unexpected "${String(peek()!.value)}" in "${source}"`);
    }

    return node;
}

function member(value: Value | undefined, name: string) : Value | undefined {
    if (value === undefined || typeof value === 'number') {
        return undefined;
    }
    if (name in value) {
        return value[name];
    }

    const lower = name.toLowerCase();
    const key = Object.keys(value).find((candidate) => candidate.toLowerCase() === lower);

    return key === undefined ? undefined : value[key];
}

function evaluateNode(node: Node, scope: Scope) : Value | undefined {
    switch (node.kind) {
        case 'number':
            return node.value;
        case 'path': {
            let value = member(scope as Value, node.names[0]!);
            for (const name of node.names.slice(1)) {
                value = member(value, name);
            }
            return value;
        }
        case 'call': {
            const args = node.args.map((arg) => evaluateNode(arg, scope));
            if (args.some((arg) => typeof arg !== 'number')) {
                return undefined;
            }

            return FUNCTIONS[node.name]!(...(args as number[]));
        }
        case 'negate': {
            const operand = evaluateNode(node.operand, scope);

            return typeof operand === 'number' ? -operand : undefined;
        }
        case 'binary': {
            const left = evaluateNode(node.left, scope);
            const right = evaluateNode(node.right, scope);
            if (typeof left !== 'number' || typeof right !== 'number') {
                return undefined;
            }

            switch (node.op) {
                case '+':
                    return left + right;
                case '-':
                    return left - right;
                case '*':
                    return left * right;
                case '/':
                    return left / right;
                case '%':
                    return left % right;
            }
        }
        // Comparisons and logic give 1 or 0; with a side that is not a number, nothing.
        case 'compare': {
            const left = evaluateNode(node.left, scope);
            const right = evaluateNode(node.right, scope);
            if (typeof left !== 'number' || typeof right !== 'number') {
                return undefined;
            }

            const holds = { '<': left < right, '<=': left <= right, '>': left > right, '>=': left >= right, '==': left === right, '!=': left !== right }[node.op];

            return holds ? 1 : 0;
        }
        case 'logic': {
            const left = evaluateNode(node.left, scope);
            const right = evaluateNode(node.right, scope);
            if (typeof left !== 'number' || typeof right !== 'number') {
                return undefined;
            }

            return (node.op === 'and' ? left !== 0 && right !== 0 : left !== 0 || right !== 0) ? 1 : 0;
        }
        case 'not': {
            const operand = evaluateNode(node.operand, scope);

            return typeof operand === 'number' ? (operand === 0 ? 1 : 0) : undefined;
        }
    }
}

/** A parsed expression. Parsing happens once; evaluation is cheap. */
export interface Expression {
    readonly source: string;
    /** Undefined when a name is unknown or a value has the wrong kind. */
    evaluate(scope: Scope) : Value | undefined;
}

const cache = new Map<string, Expression>();

/** Parses an expression, from a cache when it was seen before. Throws `ExpressionError` for bad syntax. */
export function compile(source: string) : Expression {
    let expression = cache.get(source);
    if (!expression) {
        const node = parse(source);
        expression = { source, evaluate: (scope) => evaluateNode(node, scope) };
        cache.set(source, expression);
    }
    return expression;
}
